/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { apiKeysCreateKey } from "../funcs/apiKeysCreateKey.js";
import { apiKeysDeleteKey } from "../funcs/apiKeysDeleteKey.js";
import { apiKeysListKeys } from "../funcs/apiKeysListKeys.js";
import { apiKeysRerollKey } from "../funcs/apiKeysRerollKey.js";
import { apiKeysUpdateKey } from "../funcs/apiKeysUpdateKey.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import * as operations from "../models/operations/index.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class ApiKeys extends ClientSDK {
  /**
   * Create API key
   *
   * @remarks
   * Creates a API key for the authenticated workspace.
   * Requires `teral:v1:<workspace_id>:rootKeys/*#write`.
   * The created API key cannot have more permissions than the caller.
   */
  async createKey(
    request: components.V1ApiKeysCreateKeyRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ApiKeysCreateKeyResponseBody> {
    return unwrapAsync(apiKeysCreateKey(
      this,
      request,
      options,
    ));
  }

  /**
   * Delete API key
   *
   * @remarks
   * Deletes a API key in the authenticated workspace.
   * Requires `teral:v1:<workspace_id>:rootKeys/<key_id>#delete`.
   * Use `rootKeys/*#delete` to delete any API key in the workspace.
   * Deleted keys cannot authenticate.
   */
  async deleteKey(
    request: components.V1ApiKeysDeleteKeyRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ApiKeysDeleteKeyResponseBody> {
    return unwrapAsync(apiKeysDeleteKey(
      this,
      request,
      options,
    ));
  }

  /**
   * List API keys
   *
   * @remarks
   * Lists readable API keys in the authenticated workspace, ordered by key ID.
   * Requires `teral:v1:<workspace_id>:rootKeys/<key_id>#read` for each returned key.
   * Use `rootKeys/*#read` to read all API keys. Write permission does not imply read.
   * A caller with no readable keys receives an empty page.
   *
   * Includes disabled and expired keys from the new root-key store.
   * Deleted keys are excluded. Secrets and hashes are never returned.
   * Pass the returned cursor when hasMore is true to retrieve the next page.
   */
  async listKeys(
    request: components.V1ApiKeysListKeysRequestBody,
    options?: RequestOptions,
  ): Promise<
    PageIterator<operations.ApiKeysListKeysResponse, { cursor: string }>
  > {
    return unwrapResultIterator(apiKeysListKeys(
      this,
      request,
      options,
    ));
  }

  /**
   * Reroll API key
   *
   * @remarks
   * Creates a new API key secret with the same name, enabled state, expiration, and
   * effective permissions as an existing API key in the authenticated workspace.
   * Requires `teral:v1:<workspace_id>:rootKeys/<key_id>#write`, and the caller must
   * already hold every permission of the original key. When `expiration` is not null,
   * it also requires `teral:v1:<workspace_id>:rootKeys/<key_id>#delete`.
   *
   * `expiration` controls the original key: 0 revokes it immediately, a positive value
   * keeps it valid for that many milliseconds, and null keeps its current expiration.
   */
  async rerollKey(
    request: components.V1ApiKeysRerollKeyRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ApiKeysRerollKeyResponseBody> {
    return unwrapAsync(apiKeysRerollKey(
      this,
      request,
      options,
    ));
  }

  /**
   * Update API key
   *
   * @remarks
   * Updates a API key in the authenticated workspace.
   * Requires `teral:v1:<workspace_id>:rootKeys/<key_id>#write`.
   * Use `rootKeys/*#write` to update any API key in the workspace.
   *
   * `permissions` replaces the complete permission set. Each permission must be a supported URN
   * in the authenticated workspace and within the caller's permissions.
   * An expiring API key can only update API keys that expire no later than itself.
   */
  async updateKey(
    request: components.V1ApiKeysUpdateKeyRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ApiKeysUpdateKeyResponseBody> {
    return unwrapAsync(apiKeysUpdateKey(
      this,
      request,
      options,
    ));
  }
}
